import React, { useState } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    IconButton,
    Tooltip,
    Box,
    ListItemIcon
} from '@mui/material';
import { FilterAltOff as FilterAltOffIcon } from '@mui/icons-material';
import { useThemeContext, THEME_CLASSIC } from '../theme/ThemeContext';

/**
 * DrkMultiFilterSelect - Eine generische Komponente für mehrfache Auswahl mit Filteranzeige
 * 
 * @param {Object} props
 * @param {Array} props.options - Array von Optionen in Form von { value, label, icon? }
 * @param {Array} props.selectedValues - Array der ausgewählten Werte
 * @param {Function} props.onChange - Callback-Funktion beim Ändern der Auswahl
 * @param {string} props.label - Label für das Select-Feld
 * @param {string} props.allSelectedText - Text, der angezeigt wird, wenn alle Optionen ausgewählt sind
 * @param {string} props.activeFilterText - Text vor der Anzahl der aktiven Filter
 * @param {boolean} props.showResetButton - Ob der Reset-Button angezeigt werden soll
 * @param {Function} props.onReset - Callback-Funktion für den Reset-Button
 * @param {string} props.resetTooltip - Tooltip-Text für den Reset-Button
 * @param {boolean} props.withIcons - Ob Icons in den Menüpunkten angezeigt werden sollen
 * @param {Object} props.sx - Zusätzliche Styles für die FormControl
 */
const DrkMultiFilterSelect = ({
    options = [],
    selectedValues = [],
    onChange,
    label = "Filter",
    allSelectedText = "kein Filter aktiviert",
    activeFilterText = "Aktive Filter:",
    showResetButton = true,
    onReset,
    resetTooltip = "Filter zurücksetzen",
    withIcons = false,
    sx = {},
    ...props
}) => {
    const [open, setOpen] = useState(false);
    const { currentTheme } = useThemeContext();
    const isClassic = currentTheme === THEME_CLASSIC;

    // Ob alle Optionen ausgewählt sind
    const allSelected = selectedValues.length === options.length;

    // Ob überhaupt gefiltert wird
    const isFiltering = !allSelected;

    // Theme-spezifische Farben
    const primaryColor = isClassic ? '#2E75B6' : '#eb5b28';
    const bgColor = isFiltering
        ? isClassic
            ? 'rgba(91, 155, 213, 0.1)' // Helleres Blau für Classic
            : 'rgba(228, 100, 80, 0.1)' // Leichtes Orange für Modern
        : 'inherit';

    // Style für die Badge mit der Anzahl der Filter
    const badgeStyle = {
        border: `1px solid ${primaryColor}`,
        borderRadius: '4px',
        padding: '2px 6px',
        marginLeft: '4px',
        color: primaryColor,
        fontWeight: 'bold'
    };

    // Ob der Reset-Button angezeigt werden soll (nur wenn nicht alle ausgewählt sind)
    const shouldShowResetButton = showResetButton && isFiltering;

    // Handler für die Änderung der Auswahl
    const handleChange = (event) => {
        let value = event.target.value;
        // Falls value ein String ist (zum Beispiel beim Reset) oder kein Array:
        if (!Array.isArray(value)) {
            value = value ? value.split(',') : [];
        }
        onChange(value);
    };

    // Handler für den Reset-Button
    const handleReset = () => {
        if (onReset) {
            onReset();
        } else {
            // Standardverhalten: Alle Optionen auswählen
            onChange(options.map(option => option.value));
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 180, ...sx }} size="small">
                <InputLabel id={`${label.toLowerCase().replace(/\s+/g, '-')}-filter-label`}>{label}</InputLabel>
                <Select
                    labelId={`${label.toLowerCase().replace(/\s+/g, '-')}-filter-label`}
                    id={`${label.toLowerCase().replace(/\s+/g, '-')}-filter`}
                    multiple
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    value={selectedValues}
                    onChange={handleChange}
                    input={
                        <OutlinedInput
                            label={label}
                            sx={{
                                backgroundColor: bgColor,
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: isFiltering ? primaryColor : "lightgray"
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: isFiltering ? primaryColor : "lightgray"
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: primaryColor
                                }
                            }}
                        />
                    }
                    renderValue={(selected) => {
                        if (selected.length === options.length) {
                            return allSelectedText;
                        }
                        return (
                            <span>
                                {activeFilterText} <span style={badgeStyle}>{selected.length}</span>
                            </span>
                        );
                    }}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                width: 300,
                            },
                        },
                    }}
                    {...props}
                >
                    {options.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                            <Checkbox checked={selectedValues.indexOf(option.value) > -1} />
                            {withIcons && option.icon && (
                                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, color: primaryColor }}>
                                    {option.icon}
                                </ListItemIcon>
                            )}
                            <ListItemText primary={option.label} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {shouldShowResetButton && (
                <Tooltip title={resetTooltip}>
                    <IconButton
                        onClick={handleReset}
                        size="small"
                        sx={{
                            ml: 1,
                            color: primaryColor
                        }}
                    >
                        <FilterAltOffIcon />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

export default DrkMultiFilterSelect;